import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2 } from 'lucide-react';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';

/**
 * S-12 · Enlaza un trabajo a la CONVERSACIÓN que lo originó.
 *
 * Los trabajos que nacen de "Agendar" ya vienen enlazados. Este modal es para
 * los creados a mano: permite asociarlos después al lead del CRM, y corregir un
 * enlace equivocado desenlazándolo.
 *
 * Solo se ofrecen conversaciones DEL MISMO cliente — el backend además lo
 * valida: un trabajo apuntando al chat de otra persona abriría la conversación
 * equivocada desde la ficha.
 */

/** Etiqueta legible de la temperatura del lead. */
const LEAD_LABEL: Record<string, string> = {
  CALIENTE: 'Caliente',
  TIBIO: 'Tibio',
  FRIO: 'Frío',
  CERRADO: 'Cerrado (venta)',
  NO_CERRADO: 'No cerrado',
};

export function VincularConversacionModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [seleccion, setSeleccion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const convos = useQuery({
    queryKey: ['jobs', 'conversaciones', job?.contactId],
    queryFn: () => jobsApi.listConversacionesDeContacto(job!.contactId),
    enabled: Boolean(job?.contactId),
  });

  useEffect(() => {
    if (!job) return;
    setSeleccion(job.conversationId ?? '');
    setError(null);
  }, [job]);

  const vincular = useMutation({
    mutationFn: () => jobsApi.vincularConversacion(job!.id, seleccion || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success(seleccion ? 'Trabajo enlazado a la conversación' : 'Enlace quitado');
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo cambiar el enlace'),
  });

  const cliente = job?.contact?.name ?? job?.contact?.pushName ?? job?.contact?.phone ?? 'el cliente';
  const lista = convos.data ?? [];

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title="Conversación de este trabajo"
      description={`Conversaciones de ${cliente}. Sirve para abrir el chat desde el trabajo.`}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => vincular.mutate()} loading={vincular.isPending}>
            <Link2 className="mr-1 h-4 w-4" />
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            ¿De qué conversación salió este trabajo?
          </span>
          <select
            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            value={seleccion}
            onChange={(e) => setSeleccion(e.target.value)}
          >
            <option value="">Sin conversación</option>
            {lista.map((c) => {
              const cuando = c.lastMessageAt ?? c.createdAt;
              const fecha = cuando ? new Date(cuando).toLocaleDateString() : 'sin fecha';
              const temp = c.leadStatus ? ` · ${LEAD_LABEL[c.leadStatus] ?? c.leadStatus}` : '';
              return (
                <option key={c.id} value={c.id}>
                  {fecha}
                  {temp}
                </option>
              );
            })}
          </select>
        </label>

        {lista.length === 0 && !convos.isLoading && (
          <p className="text-xs text-slate-500">
            Este cliente no tiene conversaciones en el sistema. El trabajo puede quedar sin
            enlazar: no pasa nada, solo no habrá chat que abrir desde acá.
          </p>
        )}

        {job?.conversationId && seleccion === '' && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            Va a quitar el enlace que este trabajo ya tenía. El trabajo no se borra; solo deja de
            estar asociado a esa conversación.
          </p>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
